import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Loader2 } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { ApiError } from "@/lib/api/client";
import { refreshHomeAfterAct } from "@/lib/home-query-keys";
import {
  isZoomManagementDestination,
  zoomRecordingQueryKey,
  zoomRecordingsApi,
  type ZoomRecordingResponse,
} from "@/lib/api/zoom-recordings";

/**
 * Zoom's read of the class's recording, at the foot of the recording tile.
 * The tile reads it when it opens and says nothing at rest: the tile's head
 * says the recording's state once and this never repeats it (object checklist
 * items 4 and 5). While the read is not final (Zoom is still preparing the
 * recording) or after it failed, a quiet "Check again" reads Zoom once more;
 * a read that succeeds tells Home (`refreshHomeAfterAct`) and the tile
 * (`onRead`), whose recordings it may have changed. The validated management
 * destination stays as a link at rest.
 */

/** Whether the read can still change, and why, when it did not succeed. */
type Reading = "final" | "pending" | "refused" | "failed";

const REFUSED_CODES = new Set(["token_refused", "recording_read_refused"]);

function reading(response: ZoomRecordingResponse): Reading {
  if (response.diagnostic) {
    return REFUSED_CODES.has(response.diagnostic.provider_code ?? "") ? "refused" : "failed";
  }
  const observed = response.observation.state;
  if (observed === "refused" || observed === "access_denied" || observed === "inaccessible") return "refused";
  const lifecycle = response.observation.lifecycle_state;
  if (lifecycle === "processing" || lifecycle === "delayed") return "pending";
  if (observed === "delayed" || observed === "stale" || observed === "unknown") return "pending";
  return "final";
}

const WHY: Record<Exclude<Reading, "final" | "pending">, string> = {
  refused: "Zoom refused the recording read. Check the Zoom connection before trying again.",
  failed: "The recording could not be read from Zoom.",
};

export function ZoomRecordingControls({ sessionId, onRead }: { sessionId: string; onRead?: () => void }) {
  const queryClient = useQueryClient();
  const queryKey = zoomRecordingQueryKey(sessionId);
  const recording = useQuery({
    queryKey,
    queryFn: () => zoomRecordingsApi.get(sessionId),
    retry: false,
  });
  const [checking, setChecking] = useState(false);
  const [checkFailed, setCheckFailed] = useState(false);

  if (recording.error instanceof ApiError && recording.error.status === 404) return null;
  if (recording.isPending) return null;

  async function check() {
    setChecking(true);
    setCheckFailed(false);
    try {
      const result = await zoomRecordingsApi.refresh(sessionId);
      queryClient.setQueryData<ZoomRecordingResponse>(queryKey, result);
      const refreshed = await recording.refetch();
      if (result.diagnostic && refreshed.data) {
        queryClient.setQueryData<ZoomRecordingResponse>(queryKey, {
          ...refreshed.data,
          diagnostic: result.diagnostic,
        });
      }
      if (!result.diagnostic) {
        refreshHomeAfterAct(queryClient);
        onRead?.();
      }
    } catch {
      setCheckFailed(true);
    } finally {
      setChecking(false);
    }
  }

  const response = recording.data;
  const why: Reading = checkFailed || !response ? "failed" : reading(response);
  const destination = response?.observation.destination ?? response?.observation.data?.destination;
  const managementLink = isZoomManagementDestination(destination) && (
    <Button asChild variant="link" size="sm" className="min-w-[var(--control-height-compact)] px-0 has-[>svg]:px-0">
      <a href={destination.url} target="_blank" rel="noopener noreferrer">
        {destination.label}
        <ExternalLink aria-hidden className="size-4" strokeWidth={1.75} />
        <span className="sr-only"> (opens in a new tab)</span>
      </a>
    </Button>
  );
  if (why === "final" && !managementLink) return null;

  return (
    <div className="grid justify-items-start gap-1">
      {why !== "final" && (
        <p role={why === "pending" ? undefined : "status"} className="flex flex-wrap items-center gap-x-2 text-ui text-muted-foreground">
          {why !== "pending" && WHY[why]}
          <Button type="button" variant="link" size="sm" className="min-w-[var(--control-height-compact)] px-0" disabled={checking} onClick={() => void check()}>
            {checking && <Loader2 aria-hidden className="size-4 animate-spin" strokeWidth={1.75} />}
            Check again
          </Button>
        </p>
      )}
      {managementLink}
    </div>
  );
}
